import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Dashboard from "./Dashboard";
import { Wordmark } from "../components/Nav";

function Shell({ children, wide }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-navy px-5 py-16" data-testid="admin-shell">
      <div className={"w-full " + (wide ? "max-w-md" : "max-w-md")}>
        <div className="mb-8 flex justify-center">
          <Wordmark tone="dark" />
        </div>
        {children}
      </div>
    </div>
  );
}

export function SetupScreen({ inviteToken, onReady }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Enter your email and a password.");
      return;
    }
    if (password.length < 10) {
      setError("Password must be at least 10 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/setup", { email: email.trim(), password, setup_token: inviteToken });
      onReady({ email: data.email });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <Shell>
      <div className="border border-gold/30 bg-cream p-8 sm:p-9" data-testid="admin-setup-card">
        <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-gold">First-time setup</p>
        <h1 className="mt-3 font-serif text-3xl font-light text-navy">Create your administrator account</h1>
        <p className="mt-3 text-sm leading-relaxed text-navy/65">
          You opened your private setup link. Choose the email and password you will use to sign
          in to the Chaplain at Sea administrator area. Credentials are stored securely and never
          shown publicly.
        </p>
        <form onSubmit={onSubmit} noValidate className="mt-7 grid gap-4" data-testid="admin-setup-form">
          <div>
            <label htmlFor="ad-email" className="field-label text-navy/70">Admin email</label>
            <input id="ad-email" type="email" autoComplete="username" data-testid="admin-setup-email" className="field-input mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label htmlFor="ad-pass" className="field-label text-navy/70">Password (min. 10 characters)</label>
            <input id="ad-pass" type="password" autoComplete="new-password" data-testid="admin-setup-password" className="field-input mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div>
            <label htmlFor="ad-confirm" className="field-label text-navy/70">Confirm password</label>
            <input id="ad-confirm" type="password" autoComplete="new-password" data-testid="admin-setup-confirm" className="field-input mt-1.5" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          {error && (
            <p role="alert" data-testid="admin-setup-error" className="border border-red-800/30 bg-red-50 px-4 py-3 text-sm text-red-900">
              {error}
            </p>
          )}
          <button type="submit" disabled={busy} data-testid="admin-setup-submit" className="mt-1 bg-gold px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.2em] text-navy transition-colors hover:bg-gold-light disabled:opacity-60">
            {busy ? "Creating..." : "Create account & sign in"}
          </button>
        </form>
      </div>
    </Shell>
  );
}

export function LoginScreen({ onReady }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { email: email.trim(), password });
      onReady({ email: data.email });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <Shell>
      <div className="border border-gold/30 bg-cream p-8 sm:p-9" data-testid="admin-login-card">
        <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-gold">Administrator sign in</p>
        <h1 className="mt-3 font-serif text-3xl font-light text-navy">Chaplain at Sea</h1>
        <form onSubmit={onSubmit} noValidate className="mt-7 grid gap-4" data-testid="admin-login-form">
          <div>
            <label htmlFor="li-email" className="field-label text-navy/70">Email</label>
            <input id="li-email" type="email" autoComplete="username" data-testid="admin-login-email" className="field-input mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label htmlFor="li-pass" className="field-label text-navy/70">Password</label>
            <input id="li-pass" type="password" autoComplete="current-password" data-testid="admin-login-password" className="field-input mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && (
            <p role="alert" data-testid="admin-login-error" className="border border-red-800/30 bg-red-50 px-4 py-3 text-sm text-red-900">
              {error}
            </p>
          )}
          <button type="submit" disabled={busy} data-testid="admin-login-submit" className="mt-1 bg-navy px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-slate2 disabled:opacity-60">
            {busy ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </Shell>
  );
}

function readInviteToken() {
  const match = window.location.hash.match(/#setup=([A-Za-z0-9_-]+)/);
  return match ? match[1] : "";
}

function LockedSetup() {
  return (
    <Shell>
      <div className="border border-gold/30 bg-cream p-8 sm:p-9" data-testid="admin-locked-setup">
        <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-gold">First-time setup</p>
        <h1 className="mt-3 font-serif text-3xl font-light text-navy">Private setup link required</h1>
        <p className="mt-3 text-sm leading-relaxed text-navy/65">
          First-time setup is protected. Open the private setup link provided to the site owner to
          create the administrator account. If an administrator account already exists, sign in
          with your email and password.
        </p>
      </div>
    </Shell>
  );
}

export default function AdminApp() {
  const [view, setView] = useState("checking");
  const [admin, setAdmin] = useState(null);
  const [inviteToken, setInviteToken] = useState(() => {
    const token = readInviteToken();
    if (token) {
      try {
        window.history.replaceState(null, "", window.location.pathname);
      } catch (e) {}
    }
    return token;
  });

  useEffect(() => {
    const onHash = () => {
      const token = readInviteToken();
      if (token) {
        setInviteToken(token);
        setView((v) => (v === "locked" ? "setup" : v));
        try {
          window.history.replaceState(null, "", window.location.pathname);
        } catch (e) {}
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const st = await api.get("/admin/state");
        if (!st.data.has_admin) {
          setView(inviteToken ? "setup" : "locked");
          return;
        }
        try {
          const me = await api.get("/admin/me");
          setAdmin(me.data);
          setView("dashboard");
        } catch {
          setView("login");
        }
      } catch {
        setView("login");
      }
    })();
  }, []);

  if (view === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy" data-testid="admin-checking">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold-light">Loading...</p>
      </div>
    );
  }
  if (view === "setup") return <SetupScreen inviteToken={inviteToken} onReady={(a) => { setAdmin(a); setView("dashboard"); }} />;
  if (view === "locked") return <LockedSetup />;
  if (view === "login") return <LoginScreen onReady={(a) => { setAdmin(a); setView("dashboard"); }} />;

  const logout = async () => {
    try {
      await api.post("/admin/logout");
    } catch (e) {}
    setAdmin(null);
    setView("login");
  };

  return <Dashboard admin={admin} onLogout={logout} />;
}
